import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import scheduleDataRaw from '../../../data/schedule.json';

export default function ScheduleTab() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageId, setPageId] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showAdvancedJSON, setShowAdvancedJSON] = useState(false);
    const [jsonString, setJsonString] = useState('');
    
    const [formData, setFormData] = useState({
        _documentation: scheduleDataRaw._documentation, // preserve docs
        page: { eyebrow: '', title: '', titleAccent: '', subtitle: '', eventDate: '', eventVenue: '' },
        tracks: [],
        sessions: [],
        cfpCta: { title: '', description: '', buttonText: '', buttonLink: '' }
    });

    useEffect(() => {
        fetchPageContent();
    }, []);

    const fetchPageContent = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('schedule_page_content')
            .select('*')
            .eq('page_name', 'schedule-page')
            .single();

        if (error && error.code !== 'PGRST116') { 
            if (error.code === '42P01') {
                setError("Table 'schedule_page_content' does not exist in Supabase. Please create it first using the provided SQL.");
            } else {
                setError(error.message);
            }
        } else if (data) {
            setPageId(data.id);
            setFormData({
                ...data.data,
                _documentation: data.data._documentation || scheduleDataRaw._documentation // ensure docs are kept
            });
            setError(null);
        }
        setLoading(false);
    };

    const handleImportJson = async () => {
        if (!window.confirm("This will import page content from schedule.json. Are you sure?")) return;
        setLoading(true);
        
        try {
            const { error } = await supabase.from('schedule_page_content').upsert([{
                page_name: 'schedule-page',
                data: scheduleDataRaw
            }], { onConflict: 'page_name' });
            
            if (error) throw error;
            
            alert('Import successful!');
            fetchPageContent();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        const payloadData = { ...formData };

        if (pageId) {
            const { error: updateError } = await supabase
                .from('schedule_page_content')
                .update({ data: payloadData })
                .eq('id', pageId);
            if (updateError) setError(updateError.message);
        } else {
            const { data, error: insertError } = await supabase
                .from('schedule_page_content')
                .upsert([{ page_name: 'schedule-page', data: payloadData }], { onConflict: 'page_name' })
                .select();
            if (insertError) {
                setError(insertError.message);
            } else if (data && data.length > 0) {
                setPageId(data[0].id);
            }
        }
        
        if (!error) {
            alert('Page content saved successfully!');
            setIsEditing(false);
            fetchPageContent();
        } else {
            setLoading(false);
        }
    };

    // Track helpers
    const addTrack = () => {
        setFormData({
            ...formData,
            tracks: [...formData.tracks, { id: 'new_track', label: 'New Track', icon: 'fa-star', desc: '', color: '#FFFFFF' }]
        });
    };
    
    const updateTrack = (index, field, value) => {
        const newTracks = [...formData.tracks];
        newTracks[index][field] = value;
        setFormData({ ...formData, tracks: newTracks });
    };

    const removeTrack = (index) => {
        const newTracks = [...formData.tracks];
        newTracks.splice(index, 1);
        setFormData({ ...formData, tracks: newTracks });
    };

    // Session helpers
    const addSession = () => {
        setFormData({
            ...formData,
            sessions: [...formData.sessions, { track: formData.tracks[0]?.id || '', time: '10:00 AM', type: 'talk', title: 'New Session', desc: '', speakers: [] }]
        });
    };
    
    const updateSession = (index, field, value) => {
        const newSessions = [...formData.sessions];
        newSessions[index][field] = value;
        setFormData({ ...formData, sessions: newSessions });
    };

    const removeSession = (index) => {
        const newSessions = [...formData.sessions];
        newSessions.splice(index, 1);
        setFormData({ ...formData, sessions: newSessions });
    };

    // Speaker helpers
    const addSpeaker = (sessionIndex) => {
        const newSessions = [...formData.sessions];
        if (!newSessions[sessionIndex].speakers) newSessions[sessionIndex].speakers = [];
        newSessions[sessionIndex].speakers.push({ name: '', role: '', org: '', img: '' });
        setFormData({ ...formData, sessions: newSessions });
    };

    const updateSpeaker = (sessionIndex, speakerIndex, field, value) => {
        const newSessions = [...formData.sessions];
        newSessions[sessionIndex].speakers[speakerIndex][field] = value;
        setFormData({ ...formData, sessions: newSessions });
    };

    const removeSpeaker = (sessionIndex, speakerIndex) => {
        const newSessions = [...formData.sessions];
        newSessions[sessionIndex].speakers.splice(speakerIndex, 1);
        setFormData({ ...formData, sessions: newSessions });
    };

    const uploadSpeakerImage = async (e, sessionIndex, speakerIndex) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploadingImage(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('speaker-images')
                .upload(filePath, file);

            if (uploadError) {
                throw uploadError;
            }

            const { data } = supabase.storage
                .from('speaker-images')
                .getPublicUrl(filePath);

            updateSpeaker(sessionIndex, speakerIndex, 'img', data.publicUrl);
        } catch (error) {
            alert('Error uploading image: ' + error.message);
        } finally {
            setUploadingImage(false);
        }
    };

    if (!pageId && !loading && !error) {
        return (
            <div className="card shadow-lg border-0 rounded-4 text-center py-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h4 className="text-white mb-3">No Schedule Content Found</h4>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>Please import the initial content from JSON.</p>
                <div>
                    <button onClick={handleImportJson} className="btn btn-outline-warning">
                        <i className="fa fa-download me-2"></i>Import from JSON
                    </button>
                </div>
            </div>
        );
    }

    if (isEditing) {
        return (
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Edit Schedule Content</h4>
                        <button 
                            type="button" 
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => {
                                if (!showAdvancedJSON) {
                                    setJsonString(JSON.stringify(formData, null, 2));
                                }
                                setShowAdvancedJSON(!showAdvancedJSON);
                            }}
                        >
                            <i className="fas fa-code me-2"></i> {showAdvancedJSON ? "Hide Advanced JSON" : "Show Advanced JSON Editor"}
                        </button>
                    </div>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {error && <div className="alert alert-danger">{error}</div>}
                    
                    <form onSubmit={handleSubmit}>
                        {showAdvancedJSON ? (
                            <div className="col-12 mb-4">
                                <div className="alert alert-warning mb-3">
                                    <strong>Warning:</strong> Be very careful when editing raw JSON. A syntax error will break the layout.
                                </div>
                                <label className="form-label">Raw Data (JSON)</label>
                                <textarea 
                                    className="form-control bg-dark text-warning" 
                                    style={{ border: '1px solid rgba(255,255,255,0.2)', fontFamily: 'monospace' }} 
                                    rows="25"
                                    value={jsonString}
                                    onChange={e => setJsonString(e.target.value)}
                                    onBlur={e => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            setFormData(parsed);
                                        } catch(err) {
                                            alert("Invalid JSON format! Please fix any missing quotes or commas before saving.");
                                        }
                                    }}
                                ></textarea>
                            </div>
                        ) : (
                            <>
                                <h5 className="mb-3 mt-2" style={{ color: 'var(--eventflow-primary)' }}>Hero Header (Page Details)</h5>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Eyebrow Text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.page?.eyebrow || ''} 
                                            onChange={e => setFormData({...formData, page: {...formData.page, eyebrow: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Start</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.page?.title || ''} 
                                            onChange={e => setFormData({...formData, page: {...formData.page, title: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Accent (Colored)</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.page?.titleAccent || ''} 
                                            onChange={e => setFormData({...formData, page: {...formData.page, titleAccent: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Subtitle / Description</label>
                                        <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                            value={formData.page?.subtitle || ''} 
                                            onChange={e => setFormData({...formData, page: {...formData.page, subtitle: e.target.value}})} 
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Event Date</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.page?.eventDate || ''} 
                                            onChange={e => setFormData({...formData, page: {...formData.page, eventDate: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Event Venue</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.page?.eventVenue || ''} 
                                            onChange={e => setFormData({...formData, page: {...formData.page, eventVenue: e.target.value}})} 
                                        />
                                    </div>
                                </div>
                                
                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                    <h5 className="m-0" style={{ color: 'var(--eventflow-primary)' }}>Tracks (Tabs)</h5>
                                    <button type="button" className="btn btn-sm btn-outline-info" onClick={addTrack}>+ Add Track</button>
                                </div>
                                
                                {formData.tracks.map((track, idx) => (
                                    <div className="card bg-transparent mb-3" style={{ border: '1px dashed rgba(255,255,255,0.2)' }} key={idx}>
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <h6 className="m-0 text-white-50">Track {idx + 1}</h6>
                                                <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeTrack(idx)}><i className="fas fa-trash"></i></button>
                                            </div>
                                            <div className="row g-2">
                                                <div className="col-md-2">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Track ID</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.id} onChange={e => updateTrack(idx, 'id', e.target.value)} placeholder="e.g. main" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Label</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.label} onChange={e => updateTrack(idx, 'label', e.target.value)} />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Icon (fa-xxx)</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.icon} onChange={e => updateTrack(idx, 'icon', e.target.value)} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Hex Color</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input type="color" className="form-control form-control-color bg-transparent p-1" style={{ border: '1px solid rgba(255,255,255,0.2)', width: '38px', height: '38px' }} 
                                                            value={track.color || '#ffffff'} onChange={e => updateTrack(idx, 'color', e.target.value)} />
                                                        <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                            value={track.color} onChange={e => updateTrack(idx, 'color', e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.desc} onChange={e => updateTrack(idx, 'desc', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <h5 className="mb-3 mt-4" style={{ color: 'var(--eventflow-primary)' }}>CFP Call To Action (Bottom of page)</h5>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cfpCta?.title || ''} 
                                            onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, title: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cfpCta?.description || ''} 
                                            onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, description: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Button Text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cfpCta?.buttonText || ''} 
                                            onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, buttonText: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Button Link</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cfpCta?.buttonLink || ''} 
                                            onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, buttonLink: e.target.value}})} 
                                        />
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                    
                                    <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                        <h5 className="m-0" style={{ color: 'var(--eventflow-primary)' }}>Sessions</h5>
                                        <button type="button" className="btn btn-sm btn-outline-info" onClick={addSession}>+ Add Session</button>
                                    </div>

                                    {formData.sessions.map((session, sIdx) => (
                                        <div className="card bg-transparent mb-3" style={{ border: '1px solid rgba(255,255,255,0.1)' }} key={sIdx}>
                                            <div className="card-header bg-transparent border-bottom border-secondary p-3 d-flex justify-content-between align-items-center">
                                                <h6 className="m-0 text-white-50">Session {sIdx + 1}</h6>
                                                <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeSession(sIdx)}><i className="fas fa-trash"></i></button>
                                            </div>
                                            <div className="card-body p-3">
                                                <div className="row g-2 mb-3">
                                                    <div className="col-md-3">
                                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Track</label>
                                                        <select className="form-select bg-dark text-white form-select-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                            value={session.track} onChange={e => updateSession(sIdx, 'track', e.target.value)}>
                                                            {formData.tracks.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Time</label>
                                                        <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                            value={session.time} onChange={e => updateSession(sIdx, 'time', e.target.value)} />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Type</label>
                                                        <select className="form-select bg-dark text-white form-select-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                            value={session.type} onChange={e => updateSession(sIdx, 'type', e.target.value)}>
                                                            <option value="talk">Talk</option>
                                                            <option value="keynote">Keynote</option>
                                                            <option value="panel">Panel</option>
                                                            <option value="workshop">Workshop</option>
                                                            <option value="demo">Demo</option>
                                                            <option value="ctf">CTF</option>
                                                            <option value="break">Break</option>
                                                            <option value="ceremony">Ceremony</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                                        <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                            value={session.title} onChange={e => updateSession(sIdx, 'title', e.target.value)} />
                                                    </div>
                                                    <div className="col-md-12">
                                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                                        <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                            value={session.desc} onChange={e => updateSession(sIdx, 'desc', e.target.value)} />
                                                    </div>
                                                </div>

                                                <div className="p-2 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <h6 className="m-0" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>Speakers ({session.speakers?.length || 0})</h6>
                                                        <button type="button" className="btn btn-sm btn-outline-light py-0 px-2" style={{ fontSize: '12px' }} onClick={() => addSpeaker(sIdx)}>+ Add Speaker</button>
                                                    </div>
                                                    {(session.speakers || []).map((spk, spkIdx) => (
                                                        <div className="row g-2 mb-2 pb-2 border-bottom border-secondary last-border-0" key={spkIdx}>
                                                            <div className="col-md-3">
                                                                <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                                    placeholder="Name" value={spk.name} onChange={e => updateSpeaker(sIdx, spkIdx, 'name', e.target.value)} />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                                    placeholder="Role" value={spk.role} onChange={e => updateSpeaker(sIdx, spkIdx, 'role', e.target.value)} />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                                    placeholder="Org" value={spk.org} onChange={e => updateSpeaker(sIdx, spkIdx, 'org', e.target.value)} />
                                                            </div>
                                                            <div className="col-md-2">
                                                                <input type="text" className="form-control bg-transparent text-white form-control-sm mb-1" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                                    placeholder="Image Filename/URL" value={spk.img} onChange={e => updateSpeaker(sIdx, spkIdx, 'img', e.target.value)} />
                                                                <input type="file" accept="image/*" className="form-control bg-dark text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)', fontSize: '10px' }}
                                                                    onChange={e => uploadSpeakerImage(e, sIdx, spkIdx)} disabled={uploadingImage} />
                                                            </div>
                                                            <div className="col-md-1 text-end">
                                                                <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeSpeaker(sIdx, spkIdx)}><i className="fas fa-times"></i></button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        
                        <div className="mt-4 d-flex gap-2">
                            <button type="submit" className="btn text-white fw-bold px-5" style={{ background: 'var(--eventflow-primary)' }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Page Content'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary text-white" onClick={() => setIsEditing(false)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Schedule Page Content</h4>
                    <button onClick={() => setIsEditing(true)} className="btn btn-sm text-white fw-bold" style={{ background: 'var(--eventflow-primary)' }}>
                        <i className="fa fa-edit me-2"></i>Edit Content
                    </button>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="p-4 rounded" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Hero Section Preview</h6>
                                    <h3>{formData.page?.title} <span style={{ color: 'var(--eventflow-primary)' }}>{formData.page?.titleAccent}</span></h3>
                                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>{formData.page?.subtitle}</p>
                                    <div className="d-flex gap-3 mt-3 opacity-75">
                                        <span>📅 {formData.page?.eventDate}</span>
                                        <span>📍 {formData.page?.eventVenue}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="p-4 rounded h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Tracks ({formData.tracks?.length || 0})</h6>
                                    <ul className="list-unstyled m-0">
                                        {(formData.tracks || []).map((t, i) => (
                                            <li key={i} className="mb-2 pb-2 border-bottom border-secondary last-border-0">
                                                <i className={`fa ${t.icon} me-2`} style={{ color: t.color }}></i>
                                                <strong>{t.label}</strong> <span className="text-muted ms-2">({t.id})</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="p-4 rounded h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Sessions Overview ({formData.sessions?.length || 0})</h6>
                                    <ul className="list-unstyled m-0">
                                        {Object.entries((formData.sessions || []).reduce((acc, s) => {
                                            acc[s.track] = (acc[s.track] || 0) + 1;
                                            return acc;
                                        }, {})).map(([trackId, count], i) => {
                                            const track = formData.tracks?.find(t => t.id === trackId);
                                            return (
                                                <li key={i} className="mb-2 pb-2 border-bottom border-secondary last-border-0 d-flex justify-content-between">
                                                    <span>
                                                        <i className={`fa ${track?.icon || 'fa-question'} me-2`} style={{ color: track?.color || '#fff' }}></i>
                                                        {track?.label || trackId}
                                                    </span>
                                                    <span className="badge bg-secondary">{count} sessions</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
